import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Stack, Chip } from '@mui/material';
import {
  PlaceOutlined as PlaceIcon,
  PublicOutlined as GlobeIcon,
} from '@mui/icons-material';

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

const locations = [
  {
    id: 'vadodara',
    name: 'Vadodara, Gujarat',
    tag: 'Head Office',
    blurb: 'Our central base for coordination, QA/QC auditing and inland inspection work.',
    services: ['Factory & vendor audits', 'QA/QC inspection', 'Documentation & certification', 'Inland cargo inspection'],
  },
  {
    id: 'india',
    name: 'Across India',
    tag: 'National Network',
    blurb: 'Surveyor coverage at major Indian ports and inland container depots on request.',
    services: ['Port-side surveys', 'Pre-shipment inspection', 'Stock & inventory audits', 'Sampling & tally'],
  },
  {
    id: 'worldwide',
    name: 'Worldwide',
    tag: 'Global Coordination',
    blurb: 'We arrange independent inspections at ports and facilities around the world.',
    services: ['Cross-border inspection coordination', 'Overseas loading supervision', 'Third-party verification', 'Remote-supported reporting'],
  },
];

const GlobalReach = () => {
  const [active, setActive] = useState('vadodara');
  const current = locations.find((l) => l.id === active);

  return (
    <Box sx={{ py: { xs: 7, md: 10 } }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>Global Reach</SectionLabel>
          <Typography variant="h2" gutterBottom>Inspection coverage, wherever your cargo moves</Typography>
          <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
            Headquartered in India with a base at Vadodara, InspectIQ coordinates
            inspection and survey services at ports and facilities around the world.
            Select a location to see what we cover there.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Location selector */}
          <Grid item xs={12} md={5}>
            <Stack spacing={1.5}>
              {locations.map((loc) => {
                const selected = loc.id === active;
                return (
                  <Box
                    key={loc.id}
                    onClick={() => setActive(loc.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActive(loc.id)}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'rgba(15,39,51,0.1)',
                      bgcolor: selected ? 'rgba(1,86,122,0.05)' : 'white',
                      transition: 'all 0.18s',
                      outline: 'none',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Box sx={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: selected ? 'primary.main' : 'rgba(1,86,122,0.08)', color: selected ? 'white' : 'primary.main' }}>
                      {loc.id === 'worldwide' ? <GlobeIcon /> : <PlaceIcon />}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{loc.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{loc.tag}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Grid>

          {/* Detail panel */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, height: '100%', background: 'linear-gradient(150deg, #013e5c 0%, #01567a 100%)', color: 'white' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                {current.id === 'worldwide' ? <GlobeIcon sx={{ color: 'secondary.light' }} /> : <PlaceIcon sx={{ color: 'secondary.light' }} />}
                <Typography variant="h4" sx={{ color: 'white' }}>{current.name}</Typography>
              </Stack>
              <Chip label={current.tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, mb: 2 }} />
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.88)', mb: 3 }}>{current.blurb}</Typography>
              <Typography variant="overline" sx={{ color: 'secondary.light' }}>Services here</Typography>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {current.services.map((svc, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'secondary.light', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)' }}>{svc}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GlobalReach;
